import React, { useState, useEffect } from 'react';
import {
  BookOpen, Loader2, Send, ArrowLeft, User,
  MessageSquare, CornerDownRight, Clock, Search,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { toast } from 'sonner';
import api from '../../api';
import { DueDateBadge } from '../teacher/LessonNotesPage';

const SUBJECT_COLORS = [
  { key: 'math',    from: 'from-blue-500',    to: 'to-blue-400',    bg: 'bg-blue-50',    text: 'text-blue-700',    emoji: '📐' },
  { key: 'english', from: 'from-violet-500',  to: 'to-violet-400',  bg: 'bg-violet-50',  text: 'text-violet-700',  emoji: '📖' },
  { key: 'physics', from: 'from-orange-500',  to: 'to-amber-400',   bg: 'bg-orange-50',  text: 'text-orange-700',  emoji: '⚗️' },
  { key: 'biology', from: 'from-emerald-500', to: 'to-green-400',   bg: 'bg-emerald-50', text: 'text-emerald-700', emoji: '🌿' },
  { key: 'chem',    from: 'from-pink-500',    to: 'to-rose-400',    bg: 'bg-pink-50',    text: 'text-pink-700',    emoji: '🔬' },
  { key: 'history', from: 'from-amber-500',   to: 'to-yellow-400',  bg: 'bg-amber-50',   text: 'text-amber-700',   emoji: '📜' },
  { key: 'geo',     from: 'from-teal-500',    to: 'to-teal-400',    bg: 'bg-teal-50',    text: 'text-teal-700',    emoji: '🌍' },
  { key: 'civic',   from: 'from-sky-500',     to: 'to-sky-400',     bg: 'bg-sky-50',     text: 'text-sky-700',     emoji: '⚖️' },
  { key: 'default', from: 'from-slate-400',   to: 'to-slate-500',   bg: 'bg-slate-50',   text: 'text-slate-700',   emoji: '📄' },
];

function getSubjectStyle(name = '') {
  const low = name.toLowerCase();
  if (low.includes('math'))    return SUBJECT_COLORS[0];
  if (low.includes('english')) return SUBJECT_COLORS[1];
  if (low.includes('phys'))    return SUBJECT_COLORS[2];
  if (low.includes('bio'))     return SUBJECT_COLORS[3];
  if (low.includes('chem'))    return SUBJECT_COLORS[4];
  if (low.includes('hist'))    return SUBJECT_COLORS[5];
  if (low.includes('geo'))     return SUBJECT_COLORS[6];
  if (low.includes('civ'))     return SUBJECT_COLORS[7];
  return SUBJECT_COLORS[8];
}

export default function StudentLessonNotesPage() {
  const [notes,      setNotes]      = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [selected,   setSelected]   = useState(null);
  const [loadingNote,setLoadingNote]= useState(false);
  const [newComment, setNewComment] = useState('');
  const [replyTo,    setReplyTo]    = useState(null);
  const [posting,    setPosting]    = useState(false);
  const [search,     setSearch]     = useState('');

  useEffect(() => {
    api.get('/lesson-notes')
      .then(r => setNotes(r.data || []))
      .catch(() => toast.error('Failed to load notes'))
      .finally(() => setLoading(false));
  }, []);

  const openNote = async (noteId) => {
    setLoadingNote(true);
    try {
      const r = await api.get(`/lesson-notes/${noteId}`);
      setSelected(r.data); setNewComment(''); setReplyTo(null);
      // Award XP for reading a lesson note (idempotent per note)
      api.post('/student/xp/award', { action: 'lesson_viewed', reference_id: noteId }).catch(() => {});
    } catch { toast.error('Failed to load note'); }
    finally { setLoadingNote(false); }
  };

  const handleComment = async () => {
    if (!newComment.trim()) return;
    setPosting(true);
    try {
      const r = await api.post(`/lesson-notes/${selected.id}/comments`, {
        content: newComment.trim(), parent_id: replyTo || null,
      });
      setSelected(p => ({ ...p, comments: [...(p.comments || []), r.data] }));
      setNewComment(''); setReplyTo(null);
      toast.success('Comment posted!');
    } catch (err) { toast.error(err?.detail || 'Failed to post'); }
    finally { setPosting(false); }
  };

  const filtered = notes.filter(n =>
    !search || n.topic?.toLowerCase().includes(search.toLowerCase()) ||
    n.subject?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="p-4 lg:p-6 max-w-4xl mx-auto" data-testid="student-lesson-notes-page">

      {!selected ? (
        <div className="space-y-5">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <h1 className="text-2xl font-black text-slate-900">Lesson Notes</h1>
              <p className="text-slate-500 text-sm mt-0.5">Notes shared by your teachers</p>
            </div>
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search notes..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-9 pr-4 py-2 border border-slate-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#2563eb]/20 focus:border-[#2563eb] w-full sm:w-56"
                data-testid="notes-search"
              />
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="w-8 h-8 text-[#2563eb] animate-spin" />
            </div>
          ) : notes.length === 0 ? (
            <div className="bg-white rounded-3xl border border-slate-100 py-16 text-center shadow-sm">
              <BookOpen className="w-12 h-12 mx-auto mb-3 text-slate-200" />
              <p className="font-bold text-slate-600">No lesson notes available yet</p>
              <p className="text-sm text-slate-400 mt-1">Your teachers haven't published any notes for your class</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="notes-list">
              {filtered.map(note => {
                const style = getSubjectStyle(note.subject);
                const commCount = note.comments_count || 0;
                return (
                  <button
                    key={note.id}
                    onClick={() => openNote(note.id)}
                    className="text-left bg-white rounded-3xl border border-slate-100 p-5 hover:-translate-y-0.5 hover:shadow-md transition-all duration-200 flex flex-col gap-3 group"
                    data-testid={`note-card-${note.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-11 h-11 rounded-2xl bg-gradient-to-br ${style.from} ${style.to} flex items-center justify-center text-lg flex-shrink-0 shadow-sm`}>
                        {style.emoji}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-xs font-bold uppercase tracking-wide ${style.text} mb-0.5`}>{note.subject}</p>
                        <h3 className="text-sm font-bold text-slate-900 group-hover:text-[#2563eb] transition-colors leading-tight line-clamp-2">
                          {note.topic}
                        </h3>
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-xs text-slate-400">
                      <div className="flex items-center gap-1.5">
                        <User className="w-3 h-3" />
                        <span>{note.teacher_name || note.class_name}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        {commCount > 0 && (
                          <span className="flex items-center gap-1">
                            <MessageSquare className="w-3 h-3" /> {commCount}
                          </span>
                        )}
                        {note.due_date && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" /> {note.due_date}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        /* ── Reading Mode ── */
        <div className="space-y-5">
          <button
            onClick={() => setSelected(null)}
            className="flex items-center gap-2 text-sm font-bold text-slate-500 hover:text-[#2563eb] transition-colors"
            data-testid="back-to-notes"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Notes
          </button>

          {loadingNote ? (
            <div className="flex justify-center py-20">
              <Loader2 className="w-8 h-8 text-[#2563eb] animate-spin" />
            </div>
          ) : (
            <div className="space-y-5">
              {/* Note Header */}
              {(() => {
                const style = getSubjectStyle(selected.subject);
                return (
                  <div className={`${style.bg} rounded-3xl p-6 border border-slate-100`}>
                    <div className="flex items-center gap-3 mb-3">
                      <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${style.from} ${style.to} flex items-center justify-center text-xl shadow-sm`}>
                        {style.emoji}
                      </div>
                      <div>
                        <p className={`text-xs font-bold uppercase tracking-wide ${style.text}`}>{selected.subject}</p>
                        <h2 className="text-lg font-black text-slate-900 leading-tight">{selected.topic}</h2>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-3 text-xs text-slate-500">
                      {selected.teacher_name && <span className="flex items-center gap-1.5"><User className="w-3 h-3" /> {selected.teacher_name}</span>}
                      {selected.class_name && <span>{selected.class_name}</span>}
                      {selected.due_date && <DueDateBadge dueDate={selected.due_date} />}
                    </div>
                  </div>
                );
              })()}

              {/* Content */}
              <div className="bg-white rounded-3xl border border-slate-100 p-6 shadow-sm">
                <div className="prose prose-slate max-w-none prose-p:text-base prose-p:leading-loose prose-headings:font-black">
                  <ReactMarkdown>{selected.content || ''}</ReactMarkdown>
                </div>
              </div>

              {/* Comments */}
              <div className="bg-white rounded-3xl border border-slate-100 p-6 shadow-sm space-y-4">
                <div className="flex items-center gap-2 mb-2">
                  <MessageSquare className="w-4 h-4 text-[#2563eb]" />
                  <h3 className="font-bold text-slate-800 text-sm">Discussion ({selected.comments?.length || 0})</h3>
                </div>

                {selected.comments?.length === 0 && (
                  <p className="text-sm text-slate-400 text-center py-4">No comments yet. Be the first to ask a question!</p>
                )}

                {(selected.comments || []).map(c => (
                  <div key={c.id} className={`space-y-2 ${c.parent_id ? 'ml-6 pl-4 border-l-2 border-slate-100' : ''}`}>
                    <div className="bg-slate-50 rounded-2xl p-3.5">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-xs font-bold text-slate-700">{c.author_name || 'Student'}</span>
                        <span className="text-xs text-slate-400">{c.created_at?.slice(0, 10)}</span>
                      </div>
                      <p className="text-sm text-slate-700 leading-relaxed">{c.content}</p>
                      <button
                        onClick={() => setReplyTo(c.id)}
                        className="mt-1.5 text-xs text-[#2563eb] hover:underline font-semibold flex items-center gap-1"
                      >
                        <CornerDownRight className="w-3 h-3" /> Reply
                      </button>
                    </div>
                  </div>
                ))}

                {/* Comment input */}
                {replyTo && (
                  <div className="flex items-center gap-2 text-xs text-slate-500 bg-slate-50 px-3 py-2 rounded-xl">
                    <span>Replying to comment</span>
                    <button onClick={() => setReplyTo(null)} className="text-rose-500 font-bold ml-auto">Cancel</button>
                  </div>
                )}
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newComment}
                    onChange={e => setNewComment(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleComment(); }}}
                    placeholder="Ask a question or add a comment..."
                    className="flex-1 rounded-2xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2563eb]/20 focus:border-[#2563eb]"
                    data-testid="comment-input"
                  />
                  <button
                    onClick={handleComment}
                    disabled={posting || !newComment.trim()}
                    className="bg-[#2563eb] hover:bg-[#1d58d4] text-white rounded-2xl px-4 py-2.5 flex items-center gap-2 text-sm font-bold disabled:opacity-50 transition-colors shadow-sm"
                    data-testid="post-comment-btn"
                  >
                    <Send className="w-4 h-4" />
                    {posting ? '...' : 'Post'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
